import axios, { AxiosInstance } from 'axios';
import { message } from 'antd';
import { ApiResponse } from '../types';

// Global loading counter
let loadingCount = 0;
let loadingTimer: NodeJS.Timeout | null = null;

const showLoading = () => {
  loadingCount++;
  if (loadingCount === 1) {
    // Delay showing loading to avoid flashing for fast requests
    loadingTimer = setTimeout(() => {
      if (loadingCount > 0) {
        message.loading({ content: 'Đang tải...', key: 'loading', duration: 0 });
      }
    }, 200);
  }
};

const hideLoading = () => {
  loadingCount--;
  if (loadingCount <= 0) {
    loadingCount = 0;
    if (loadingTimer) {
      clearTimeout(loadingTimer);
      loadingTimer = null;
    }
    message.destroy('loading');
  }
};

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/v1`,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token and show loading
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        // Show loading for non-GET requests or important GET requests
        if (config.method !== 'get' || config.url?.includes('/admin/')) {
          showLoading();
        }
        
        return config;
      },
      (error) => {
        hideLoading();
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle token refresh and errors
    this.client.interceptors.response.use(
      (response) => {
        hideLoading();
        return response;
      },
      async (error) => {
        hideLoading();
        
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
              const response = await axios.post(`${this.client.defaults.baseURL}/admin/refresh`, {
                refresh_token: refreshToken,
              });

              const { access_token } = response.data.data;
              localStorage.setItem('access_token', access_token);

              originalRequest.headers.Authorization = `Bearer ${access_token}`;
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, redirect to login
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!');
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        // Show error message
        const errorMessage = error.response?.data?.message || error.message || 'Có lỗi xảy ra!';
        
        // Show error with more context
        console.log('API Client Error:', error);
        console.log('Error response:', error.response);
        console.log('Error message:', errorMessage);
        
        if (error.response?.status === 400) {
          console.log('Showing 400 error message');
          alert(`❌ Lỗi dữ liệu: ${errorMessage}`);
        } else if (error.response?.status === 401) {
          console.log('Showing 401 error message');
          alert('❌ Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!');
        } else if (error.response?.status === 403) {
          console.log('Showing 403 error message');
          alert('❌ Bạn không có quyền thực hiện thao tác này!');
        } else if (error.response?.status === 404) {
          console.log('Showing 404 error message');
          alert('❌ Không tìm thấy dữ liệu!');
        } else if (error.response?.status >= 500) {
          console.log('Showing 500+ error message');
          alert(`❌ Lỗi máy chủ: ${errorMessage}`);
        } else {
          console.log('Showing generic error message');
          alert(`❌ Lỗi: ${errorMessage}`);
        }

        return Promise.reject(error);
      }
    );
  }

  // Generic request methods
  // Backend uses ApiSuccess<T> which is a tuple struct, so it serializes T directly
  async get<T>(url: string): Promise<T> {
    const response = await this.client.get<T>(url);
    return response.data;
  }

  async post<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.post<T>(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.put<T>(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.client.delete<T>(url);
    return response.data;
  }

  // Auth methods - these return the full response, not just data
  async login(credentials: { username: string; password: string }) {
    const response = await this.client.post('/admin/login', credentials);
    return response.data; // This already contains the full response
  }

  async refreshToken(refreshToken: string) {
    const response = await this.client.post('/admin/refresh', { refresh_token: refreshToken });
    return response.data;
  }

  async getMe() {
    const response = await this.client.get('/admin/me');
    return response.data;
  }
}

export const apiClient = new ApiClient();
